import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Navbar.css';

const NAV_LINKS = [
  { to: '/',        labelKey: 'home',     icon: '🏠' },
  { to: '/gallery', labelKey: 'gallery',  icon: '🖼️' },
  { to: '/memories', labelKey: 'memories', icon: '📸' },
  { to: '/rent',    labelKey: 'book',     icon: '📅' },
  { to: '/reviews', labelKey: 'reviews',  icon: '⭐' },
  { to: '/contact', labelKey: 'contact',  icon: '✉️' },
];

export default function Navbar() {
  const { user, signOut } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const { pathname } = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastScroll = useRef(0);

  /* ── Show / hide on scroll ── */
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      setHidden(y > 300 && y > lastScroll.current);
      lastScroll.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Close mobile menu on route change ── */
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  /* ── Lock body scroll when menu is open ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleSignOut = async () => {
    await signOut();
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        className={[
          'nav',
          scrolled && 'nav--scrolled',
          hidden && 'nav--hidden',
        ].filter(Boolean).join(' ')}
      >
        <div className="nav__inner">
          {/* Logo */}
          <Link to="/" className="nav__logo">
            <span className="nav__logo-icon">🌲</span>
            <span className="nav__logo-text">
              Cabana <span className="nav__logo-accent">Buru</span>
            </span>
          </Link>

          {/* Desktop links */}
          <ul className="nav__links">
            {NAV_LINKS.map(({ to, labelKey }) => (
              <li key={to}>
                <Link
                  to={to}
                  className={`nav__link${pathname === to ? ' nav__link--active' : ''}`}
                >
                  {t(`nav.${labelKey}`).toUpperCase()}
                  <span className="nav__link-bar" />
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop auth */}
          <div className="nav__auth">
            <button 
                onClick={toggleLanguage} 
                className="nav__lang-toggle"
                title="Switch Language"
            >
                {language === 'en' ? 'RO' : 'EN'}
            </button>
            <div className="nav__auth-divider"></div>

            {user ? (
              <>
                <Link to="/account" className="nav__auth-user">
                  <span className="nav__auth-avatar">
                    {user.full_name?.[0]?.toUpperCase() || '👤'}
                  </span>
                  <span className="nav__auth-name">{user.full_name?.split(' ')[0] || t('nav.myAccount')}</span>
                </Link>
                <button onClick={handleSignOut} className="nav__auth-logout" title={t('nav.signOut')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav__btn nav__btn--ghost">{t('nav.signIn')}</Link>
                <Link to="/register" className="nav__btn nav__btn--fill">{t('nav.signUp')}</Link>
              </>
            )}
          </div>

          {/* Hamburger */}
          <button
            className={`nav__burger${menuOpen ? ' nav__burger--open' : ''}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      <div className={`nav-mobile${menuOpen ? ' nav-mobile--open' : ''}`}>
        <div className="nav-mobile__bg" onClick={() => setMenuOpen(false)} />
        <div className="nav-mobile__panel">
          <div className="nav-mobile__header">
            <span className="nav-mobile__title">{t('nav.menu')}</span>
            <button className="nav-mobile__close" onClick={() => setMenuOpen(false)}>✕</button>
          </div>

          <ul className="nav-mobile__links">
            {NAV_LINKS.map(({ to, labelKey, icon }, i) => (
              <li key={to} style={{ animationDelay: `${0.08 * i}s` }}>
                <Link
                  to={to}
                  className={`nav-mobile__link${pathname === to ? ' nav-mobile__link--active' : ''}`}
                >
                  <span className="nav-mobile__link-icon">{icon}</span>
                  <span>{t(`nav.${labelKey}`).toUpperCase()}</span>
                </Link>
              </li>
            ))}
            <li style={{ animationDelay: `${0.08 * NAV_LINKS.length}s` }}>
                <button
                  onClick={toggleLanguage}
                  className="nav-mobile__link nav-mobile__link--lang"
                >
                  <span className="nav-mobile__link-icon">🌍</span>
                  <span>{language === 'en' ? 'Switch to Romanian' : 'Schimbă în Engleză'}</span>
                </button>
            </li>
          </ul>

          <div className="nav-mobile__divider" />

          <div className="nav-mobile__auth">
            {user ? (
              <>
                <Link to="/account" className="nav-mobile__link">
                  <span className="nav-mobile__link-icon">👤</span>
                  <span>{t('nav.myAccount')}</span>
                </Link>
                <button onClick={handleSignOut} className="nav-mobile__link nav-mobile__link--logout">
                  <span className="nav-mobile__link-icon">🚪</span>
                  <span>{t('nav.signOut')}</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-mobile__link">
                  <span className="nav-mobile__link-icon">🔑</span>
                  <span>{t('nav.signIn')}</span>
                </Link>
                <Link to="/register" className="nav-mobile__link nav-mobile__link--signup">
                  <span className="nav-mobile__link-icon">✨</span>
                  <span>{t('nav.signUp')}</span>
                </Link>
              </>
            )}
          </div>

          <div className="nav-mobile__footer">
            <span>🌲</span>
            <span>Cabana Buru — {t('nav.retreat')}</span>
          </div>
        </div>
      </div>
    </>
  );
}
